/**
 * createFromTemplate — framework-agnostic factory for the editor's "Add" panel.
 *
 * Creates a new SceneObject from a template descriptor, parented to an
 * explicit SceneObject ref (or scene root if not provided). Handles primitive
 * mesh attachment and default behavior registration, but does NOT:
 *   - record undo (caller does that, e.g. with CreateObjectCommand)
 *   - perform selection-aware parent resolution (caller does that — e.g.
 *     sandbox's getActivePageForSelection wrapper)
 *   - know about Pages, particle presets, or other sandbox-specific templates
 */

import type { TraceScene } from '../../core/TraceScene';
import type { SceneObject } from '../../core/SceneObject';
import { getBuiltInSrc, createPrimitiveWithMaterial } from '../../export/BuiltInPrimitives';
import { getBehaviorConstructor } from '../../behaviors/BehaviorRegistry';
import { Logger } from '../../utils/Logger';
import type { ObjectTemplate, TemplateDescriptor } from './templates';
import { OBJECT_TEMPLATES } from './templates';

export interface CreateFromTemplateOptions {
    /**
     * Parent to attach the new object under. If not provided, the object is
     * parented to the scene root.
     */
    parent?: SceneObject | null;

    /**
     * Override the default name from the template descriptor. The final name
     * is always deduplicated against existing siblings by TraceScene.
     */
    name?: string;

    /**
     * When creating a primitive, offset the object so the bottom of its
     * hierarchy bounding box rests at world y=0, whatever the parent's pose
     * (a tilted or scaled parent included). Defaults to true — matches the
     * sandbox behavior users expect when dropping a new cube.
     */
    groundAlign?: boolean;

    /** Data for the template's behaviors, by `@type`, given to each as it is added. */
    behaviorData?: Readonly<Record<string, Record<string, unknown>>>;
}

/**
 * Move `object` along world y until the bottom of its hierarchy's bounds is at y = 0.
 * The bound is a world value, so the lift is a world move: along a tilted or
 * scaled parent's local y it would land elsewhere.
 * Shared with the authoring operations; not exported from `trace-viewer/editor`.
 */
export function restOnGround(object: SceneObject): void {
    const node = object.transform;
    node.computeWorldMatrix(true);
    const { min } = node.getHierarchyBoundingVectors(true);
    if (!Number.isFinite(min.y)) return;
    const at = node.getAbsolutePosition().clone();
    at.y -= min.y;
    node.setAbsolutePosition(at);
    node.computeWorldMatrix(true);
}

/**
 * Create a new SceneObject from a template. Resolves to the created object
 * once it and its default behaviors are fully initialized. A step that fails
 * deletes the object and rethrows.
 */
export async function createFromTemplate(
    scene: TraceScene,
    template: ObjectTemplate | TemplateDescriptor,
    options: CreateFromTemplateOptions = {},
): Promise<SceneObject> {
    const descriptor = typeof template === 'string' ? OBJECT_TEMPLATES[template] : template;
    if (!descriptor) {
        const templateId = typeof template === 'string' ? template : template.template;
        throw new Error(`Unknown object template: ${templateId}`);
    }

    const { parent = null, name, groundAlign = true, behaviorData = {} } = options;

    const baseName = name ?? descriptor.defaultName;
    const src = descriptor.primitive ? getBuiltInSrc(descriptor.primitive) : undefined;

    const newObject = await scene.createSceneObject(baseName, parent ?? undefined, src);

    // A step that fails takes the half-made object back with it.
    try {
        // Attach the primitive mesh (ephemeral — regenerated from src on export)
        // and register the Renderer behavior so the editor sees materials/slots.
        if (descriptor.primitive) {
            const mesh = createPrimitiveWithMaterial(
                descriptor.primitive,
                newObject.babylonScene,
                newObject.name,
            );
            mesh.parent = newObject.transform;
        }

        // Register behaviors in order. Unknown behavior types are logged and skipped
        // rather than throwing — missing behaviors indicate a registry gap, not a
        // template misuse, and we prefer a partially-created object to a mid-op throw.
        for (const behaviorType of descriptor.behaviors) {
            const Ctor = getBehaviorConstructor(behaviorType);
            if (!Ctor) {
                Logger.warn(`[createFromTemplate] Unknown behavior "${behaviorType}" for template "${descriptor.template}"`);
                continue;
            }
            await newObject.addBehavior(Ctor, behaviorData[behaviorType]);
        }

        // Ground-align for primitives: offset so the bottom of the mesh sits at y=0.
        // Matches the sandbox drop-in behavior. Text and empty objects skip this.
        if (groundAlign && descriptor.primitive) restOnGround(newObject);
    } catch (error) {
        if (!newObject.transform.isDisposed()) scene.deleteSceneObject(newObject);
        throw error;
    }

    return newObject;
}
